import axios, { AxiosError } from 'axios'
import config from 'config'

import { OctaneLogger } from '../../integrations/datadog/OctaneLogger'

export interface NADAAccessoriesRequest {
    nada_id?: string | null
    automobile_id?: number
    dealer_state?: string | null
}

export interface NADAAccessory {
    acccode: string
    accdesc: string
    tradein: number
    retail: number
    loan: number
    isincluded: boolean
    isadded: boolean
    accessorycategory: string
    includes: string[]
    excludes: string[]
}

interface RawNADAAccessory {
    acccode: string
    accdesc: string
    tradein: string | number
    retail: string | number
    loan: string | number
    isincluded: boolean
    isadded: boolean
    accessorycategory: string
    includes: string | string[]
    excludes: string | string[]
}

function parseStringOrArray(value: string | string[]): string[] {
    if (Array.isArray(value)) {
        return value
    }

    if (typeof value === 'string' && value.length > 0) {
        return value.split(',').map((item) => item.trim())
    }

    return []
}

function normalizeAccessory(raw: RawNADAAccessory): NADAAccessory {
    return {
        acccode: raw.acccode,
        accdesc: raw.accdesc,
        tradein: parseFloat(raw.tradein as string) || 0,
        retail: parseFloat(raw.retail as string) || 0,
        loan: parseFloat(raw.loan as string) || 0,
        isincluded: raw.isincluded,
        isadded: raw.isadded,
        accessorycategory: raw.accessorycategory,
        includes: parseStringOrArray(raw.includes),
        excludes: parseStringOrArray(raw.excludes),
    }
}

export default async function postNADAAccessories(
    request: NADAAccessoriesRequest,
): Promise<NADAAccessory[]> {
    const url = `${config.API_BASE_URL}/api/v3/automobile/nada-accessories/`

    try {
        const { data } = await axios.post(url, request, {
            withCredentials: true,
        })

        const raw: RawNADAAccessory[] = Array.isArray(data)
            ? data
            : (data?.accessories ?? [])

        return raw.map(normalizeAccessory)
    } catch (error) {
        const axiosError = error as AxiosError<{ error: string }>

        if (axiosError.response?.data?.error) {
            throw new Error(axiosError.response.data.error)
        }

        OctaneLogger.axiosError(
            `Failed NADA accessories lookup: ${axiosError.message}`,
            axiosError,
        )

        throw new Error(axiosError.message)
    }
}
